import {
  FETCH_WORKFLOWS_PENDING,
  FETCH_WORKFLOWS_FULFILLED,
  FETCH_WORKFLOWS_REJECTED,
  FETCH_WORKFLOW_PENDING,
  FETCH_WORKFLOW_FULFILLED,
  FETCH_WORKFLOW_REJECTED,
  SILENT_FETCH_WORKFLOW_FULFILLED,
  INIT_WORKFLOW_PENDING,
  INIT_WORKFLOW_FULFILLED,
  INIT_WORKFLOW_REJECTED,
  PERFORM_WORKFLOW_STEP_ACTIONS_PENDING,
  PERFORM_WORKFLOW_STEP_ACTIONS_FULFILLED,
  PERFORM_WORKFLOW_STEP_ACTIONS_REJECTED,
  WORKFLOW_STEP_ERROR_RECEIVED,
  INIT_SITE_CREATION_WORKFLOW_PENDING,
  INIT_SITE_CREATION_WORKFLOW_FULFILLED,
  INIT_SITE_CREATION_WORKFLOW_REJECTED,
  WORKFLOW_SCHEMA_ERROR,
  WORKFLOW_STEP_ROLLBACK_PENDING,
  WORKFLOW_STEP_ROLLBACK_FAILED,
  WORKFLOW_STEP_ROLLBACK_SUCCESSFUL,
} from "./constants";

import { merge, cloneDeep } from "lodash";
import { AnyAction, combineReducers } from "redux";
import { Err } from "../../../typings";

// TODO: describe correct type in API facade
// This type contains properties that don't exist in type returned from server.
export type Workflow = {
  id: string;
  status:
    | "SUCCESSFUL"
    | "RUNNING"
    | "WAITING"
    | "WAITING_SOURCE_DATA_INPUT"
    | "FAILED";
  siteId: string;
  customerId: string;
  creationTimeUtc: string;
  updateTimeUtc: string;
  workflowInstanceDescription: string;
  workflowType: string;
  steps: WorkflowStep[];
  createdBy: string;
  csStatus?: string;
};

// TODO: describe correct type in API facade
// This type contains properties that don't exist in type returned from server.
export type WorkflowStep = {
  id: number;
  status: "SUCCESSFUL" | "RUNNING" | "WAITING" | "FAILED" | "SERVER_PROCESSING";
  title: string;
  descriptionMd: string;
  actions: WorkflowAction[];
  isInputStep?: boolean;
  error?: string;
  pending?: boolean;
  shouldSwitchAutomatically?: boolean;
};

// TODO: describe correct type in API facade
// This type contains properties that don't exist in type returned from server.
export type WorkflowAction = {
  id: string;
  type: string;
  value: any;
  label: string;
  defaultValue: any;
  regexp?: string; // for TEXT action
  options?: string[]; // list of items for SELECT actions
  defaultToday?: boolean; //use current browser date as defaultValue for DATE action
  description?: string;
};

export type WorkflowState = {
  pending: boolean;
  errors: Err[] | null;
  entry: Workflow;
};

type WorkflowsHash = { [key: string]: WorkflowState };

export type WorkflowsState = {
  pending: boolean;
  error: Err | null;
  entries: WorkflowsHash;
};

function pending(state = false, action: AnyAction): boolean {
  switch (action.type) {
    case FETCH_WORKFLOWS_PENDING:
    case FETCH_WORKFLOW_PENDING:
    case INIT_WORKFLOW_PENDING:
    case INIT_SITE_CREATION_WORKFLOW_PENDING:
    case WORKFLOW_STEP_ROLLBACK_PENDING:
      return true;
    case FETCH_WORKFLOWS_FULFILLED:
    case FETCH_WORKFLOW_FULFILLED:
    case FETCH_WORKFLOWS_REJECTED:
    case FETCH_WORKFLOW_REJECTED:
    case PERFORM_WORKFLOW_STEP_ACTIONS_FULFILLED:
    case INIT_WORKFLOW_FULFILLED:
    case INIT_SITE_CREATION_WORKFLOW_FULFILLED:
    case PERFORM_WORKFLOW_STEP_ACTIONS_REJECTED:
    case INIT_WORKFLOW_REJECTED:
    case INIT_SITE_CREATION_WORKFLOW_REJECTED:
    case WORKFLOW_SCHEMA_ERROR:
    case WORKFLOW_STEP_ROLLBACK_SUCCESSFUL:
    case WORKFLOW_STEP_ROLLBACK_FAILED:
      return false;
    default:
      return state;
  }
}

function error(state: Err | null = null, action: AnyAction): Err | null {
  switch (action.type) {
    case FETCH_WORKFLOWS_REJECTED:
    case INIT_WORKFLOW_REJECTED:
    case INIT_SITE_CREATION_WORKFLOW_REJECTED:
    case WORKFLOW_SCHEMA_ERROR:
    case WORKFLOW_STEP_ROLLBACK_FAILED:
      return action.payload;
    case PERFORM_WORKFLOW_STEP_ACTIONS_REJECTED:
    case FETCH_WORKFLOWS_PENDING:
    case FETCH_WORKFLOWS_FULFILLED:
    case INIT_WORKFLOW_PENDING:
    case INIT_WORKFLOW_FULFILLED:
    case PERFORM_WORKFLOW_STEP_ACTIONS_PENDING:
    case PERFORM_WORKFLOW_STEP_ACTIONS_FULFILLED:
    case INIT_SITE_CREATION_WORKFLOW_PENDING:
    case INIT_SITE_CREATION_WORKFLOW_FULFILLED:
    case WORKFLOW_STEP_ROLLBACK_PENDING:
    case WORKFLOW_STEP_ROLLBACK_SUCCESSFUL:
      return null;
    default:
      return state;
  }
}

function entries(state: WorkflowsHash = {}, action: AnyAction): WorkflowsHash {
  switch (action.type) {
    case FETCH_WORKFLOW_FULFILLED: {
      const workflow = action.payload;

      return {
        ...state,
        [workflow.id]: createWorkflowState(workflow),
      };
    }
    case SILENT_FETCH_WORKFLOW_FULFILLED: {
      // update only if updateTimeUtc differs

      const newWorkflow = action.payload;
      const workflowId = newWorkflow.id;
      const existingWorkflow = state[workflowId] && state[workflowId].entry;

      if (
        existingWorkflow &&
        existingWorkflow.updateTimeUtc === newWorkflow.updateTimeUtc
      ) {
        return state;
      }

      return {
        ...state,
        [workflowId]: createWorkflowState(newWorkflow),
      };
    }
    case FETCH_WORKFLOWS_FULFILLED:
      // replace all workflows
      return action.payload.reduce(
        (state: WorkflowsHash, workflow: Workflow) => ({
          ...state,
          [workflow.id]: createWorkflowState(workflow),
        }),
        {},
      );
    case PERFORM_WORKFLOW_STEP_ACTIONS_FULFILLED:
    case INIT_WORKFLOW_FULFILLED:
    case INIT_SITE_CREATION_WORKFLOW_FULFILLED: {
      // replace a single workflow
      const workflow = action.payload;
      return {
        ...state,
        [workflow.id]: createWorkflowState(resetPendingStepFlags(workflow)),
      };
    }
    case PERFORM_WORKFLOW_STEP_ACTIONS_REJECTED: {
      const workflowState = state[action.payload.workflowId];
      const workflow = workflowState && workflowState.entry;
      if (!workflow) {
        return state;
      }

      // reset errors and pending flag
      return {
        ...state,
        [workflow.id]: createWorkflowState(resetPendingStepFlags(workflow)),
      };
    }
    case WORKFLOW_STEP_ERROR_RECEIVED: {
      const err = action.payload;
      const workflowState = state[err.payload.workflowId];
      const workflow = workflowState && workflowState.entry;
      if (!workflow) {
        return state;
      }

      // add an error to a step of existing workflow (overwrite existing)
      // TODO: add errors to actions and accumulate them
      return {
        ...state,
        [workflow.id]: createWorkflowState(
          mergeStepIntoWorkflow(workflow, err.payload.stepId, {
            error: err.message,
          } as WorkflowStep),
        ),
      };
    }
    case FETCH_WORKFLOWS_PENDING:
    case FETCH_WORKFLOWS_REJECTED:
    case INIT_WORKFLOW_REJECTED:
    case INIT_SITE_CREATION_WORKFLOW_REJECTED:
      return {};
    case PERFORM_WORKFLOW_STEP_ACTIONS_PENDING: {
      const { workflowId, stepId } = action.payload;
      const workflow = state[workflowId].entry;
      return {
        ...state,
        [workflowId]: createWorkflowState(
          mergeStepIntoWorkflow(workflow, stepId, {
            pending: true,
          } as WorkflowStep),
          null,
          true,
        ),
      };
    }
    case INIT_WORKFLOW_PENDING:
    case INIT_SITE_CREATION_WORKFLOW_PENDING:
    default:
      return state;
  }
}

function createWorkflowState(
  entry: Workflow,
  errors: Err[] | null = null,
  pending = false,
): WorkflowState {
  return { entry, errors, pending };
}

// each step has non-contract pending field, that we use to identify, that the step is pending
// this function resets this flags for each step
function resetPendingStepFlags(wf: Workflow) {
  return {
    ...wf,
    steps: wf.steps.map((step) => ({
      ...step,
      pending: false,
    })),
  };
}

function mergeStepIntoWorkflow(
  workflow: Workflow,
  stepId: number,
  step: WorkflowStep,
): Workflow {
  const existingStep = workflow.steps[stepId - 1];
  const newWorkflow = cloneDeep(workflow);
  newWorkflow.steps[stepId - 1] = merge(existingStep, step);
  return newWorkflow;
}

export default combineReducers({ pending, error, entries });

export function getWorkflowStateById(
  state: WorkflowsState,
  workflowId: string,
): WorkflowState {
  return state.entries[workflowId] || null;
}

export function getWorkflowById(
  state: WorkflowsState,
  workflowId: string,
): Workflow {
  const workflowState = getWorkflowStateById(state, workflowId);
  return (workflowState && workflowState.entry) || null;
}

export const getWorkflowsList = (workflowsState: WorkflowsState): Workflow[] =>
  Object.keys(workflowsState.entries).map(
    (key) => workflowsState.entries[key].entry,
  );
export const getWorkflowsPending = (workflowsState: WorkflowsState): boolean =>
  workflowsState.pending;
export const getWorkflowsError = (workflowsState: WorkflowsState): Err | null =>
  workflowsState.error;
